import React, { useState, useEffect } from 'react';
import { Card, Button, Space, Typography, Alert, List, Badge, Divider } from 'antd';
import { 
  BugOutlined, 
  ReloadOutlined, 
  CheckCircleOutlined,
  ExclamationCircleOutlined,
  DisconnectOutlined,
  PlayCircleOutlined
} from '@ant-design/icons';

const { Title, Text } = Typography;

const SocketTest: React.FC = () => {
  const [socket, setSocket] = useState<any>(null);
  const [isConnected, setIsConnected] = useState(false);
  const [connectionState, setConnectionState] = useState<string>('disconnected');
  const [logs, setLogs] = useState<string[]>([]);
  const [testResults, setTestResults] = useState<any[]>([]);

  const addLog = (message: string) => {
    const timestamp = new Date().toLocaleTimeString();
    setLogs(prev => [`[${timestamp}] ${message}`, ...prev].slice(0, 20));
  };

  useEffect(() => {
    const initSocket = () => {
      if ((window as any).io) {
        const socketInstance = (window as any).io;
        setSocket(socketInstance);
        addLog('Socket.IO 实例已创建');

        // 监听连接事件
        socketInstance.on('connect', () => {
          addLog('✅ 连接成功');
          setIsConnected(true);
          setConnectionState('connected');
        });

        socketInstance.on('disconnect', (reason: string) => {
          addLog(`❌ 断开连接: ${reason}`);
          setIsConnected(false);
          setConnectionState('disconnected');
        });

        socketInstance.on('connect_error', (error: any) => {
          addLog(`❌ 连接错误: ${error.message || '未知错误'}`);
          setIsConnected(false);
          setConnectionState('error');
        });

        socketInstance.on('reconnect', (attemptNumber: number) => {
          addLog(`🔄 重连成功，尝试次数: ${attemptNumber}`);
          setIsConnected(true);
          setConnectionState('reconnected');
        });

        socketInstance.on('reconnect_attempt', (attemptNumber: number) => {
          addLog(`🔄 重连尝试: ${attemptNumber}`);
          setConnectionState('reconnecting');
        });

        socketInstance.on('reconnect_error', (error: any) => {
          addLog(`❌ 重连错误: ${error.message || '未知错误'}`);
          setConnectionState('reconnect_error');
        });

        socketInstance.on('reconnect_failed', () => {
          addLog('❌ 重连失败');
          setConnectionState('reconnect_failed');
        });

        // 检查初始连接状态
        setIsConnected(socketInstance.connected);
        setConnectionState(socketInstance.connected ? 'connected' : 'disconnected');
        addLog(`初始状态: ${socketInstance.connected ? '已连接' : '未连接'}`);
      } else {
        addLog('❌ Socket.IO 未加载');
      }
    };

    // 延迟初始化
    const timer = setTimeout(initSocket, 1000);
    return () => clearTimeout(timer);
  }, []);

  const handleConnect = () => {
    if (socket) {
      addLog('🔄 手动连接...');
      socket.connect();
    }
  };

  const handleDisconnect = () => {
    if (socket) {
      addLog('🔄 手动断开...');
      socket.disconnect();
    }
  };

  const handleTestPing = () => {
    if (socket && isConnected) {
      addLog('📡 发送ping测试...');
      socket.emit('ping', { timestamp: Date.now() }, (response: any) => {
        addLog(`📡 ping响应: ${JSON.stringify(response)}`);
        setTestResults(prev => [{
          type: 'ping',
          timestamp: new Date(),
          success: true,
          response
        }, ...prev].slice(0, 10));
      });
    } else {
      addLog('❌ Socket未连接，无法发送ping');
    }
  };

  const handleTestMessage = () => {
    if (socket && isConnected) {
      addLog('📨 发送测试消息...');
      socket.emit('test', { message: 'Hello Server!', timestamp: Date.now() }, (response: any) => {
        addLog(`📨 消息响应: ${JSON.stringify(response)}`);
        setTestResults(prev => [{
          type: 'message',
          timestamp: new Date(),
          success: true,
          response
        }, ...prev].slice(0, 10));
      });
    } else {
      addLog('❌ Socket未连接，无法发送消息');
    }
  };

  const clearLogs = () => {
    setLogs([]);
    setTestResults([]);
  };

  const getStatusColor = () => {
    switch (connectionState) {
      case 'connected': return 'success';
      case 'reconnected': return 'success';
      case 'reconnecting': return 'processing';
      case 'error': return 'error';
      case 'reconnect_error': return 'error';
      case 'reconnect_failed': return 'error';
      default: return 'default';
    }
  };

  const getStatusIcon = () => {
    switch (connectionState) {
      case 'connected':
      case 'reconnected':
        return <CheckCircleOutlined />;
      case 'reconnecting':
        return <ReloadOutlined spin />;
      case 'error':
      case 'reconnect_error':
      case 'reconnect_failed':
        return <ExclamationCircleOutlined />;
      default:
        return <DisconnectOutlined />;
    }
  };

  return (
    <div style={{ padding: '20px' }}>
      <Card>
        <div style={{ marginBottom: '20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Title level={2}>
              <BugOutlined /> Socket.IO 连接测试
            </Title>
            <Text type="secondary">测试Socket.IO连接和通信</Text>
          </div>
          <Space>
            <Button icon={<PlayCircleOutlined />} onClick={handleConnect} disabled={!socket || isConnected}>
              连接
            </Button>
            <Button icon={<DisconnectOutlined />} onClick={handleDisconnect} disabled={!socket || !isConnected}>
              断开
            </Button>
            <Button onClick={clearLogs}>
              清空日志
            </Button>
          </Space>
        </div>

        {/* 连接状态 */}
        <Alert
          message={`连接状态: ${isConnected ? '已连接' : '未连接'}`}
          description={`状态: ${connectionState} | Socket ID: ${socket?.id || '无'}`}
          type={isConnected ? 'success' : 'error'}
          style={{ marginBottom: '20px' }}
        />

        {/* 测试按钮 */}
        <Card title="功能测试" size="small" style={{ marginBottom: '20px' }}>
          <Space>
            <Button onClick={handleTestPing} disabled={!isConnected}>
              测试 Ping
            </Button>
            <Button onClick={handleTestMessage} disabled={!isConnected}>
              测试消息
            </Button>
          </Space>
        </Card>

        {/* 测试结果 */}
        {testResults.length > 0 && (
          <Card title="测试结果" size="small" style={{ marginBottom: '20px' }}>
            <List
              dataSource={testResults}
              renderItem={(item, index) => (
                <List.Item>
                  <Badge status={item.success ? 'success' : 'error'} />
                  <Text code>{item.type}</Text>
                  <Text type="secondary">{item.timestamp.toLocaleTimeString()}</Text>
                  <Text>{JSON.stringify(item.response)}</Text>
                </List.Item>
              )}
            />
          </Card>
        )}

        {/* 连接日志 */}
        <Card title="连接日志" size="small">
          <List
            dataSource={logs}
            renderItem={(log, index) => (
              <List.Item>
                <Text code style={{ fontSize: '12px' }}>{log}</Text>
              </List.Item>
            )}
          />
        </Card>
      </Card>
    </div>
  );
};

export default SocketTest;














